// Bons d'intervention (module optionnel « bons_intervention », 31/10/2026) : référentiels communs au serveur et à l'interface.
// Réglementation des fluides frigorigènes : sources et dates de consultation dans docs/REGLEMENTATION-BONS-INTERVENTION.md.
// Aucun chiffre de loi ici qui n'ait été lu dans un texte officiel ; ce qui reste incertain est marqué « à vérifier ».

export const TYPES_BON = ['depannage', 'entretien_preventif', 'controle', 'mise_en_service', 'installation'] as const;
export type TypeBon = typeof TYPES_BON[number];
export const LIBELLES_TYPE_BON: Record<TypeBon, string> = {
  depannage: 'Dépannage',
  entretien_preventif: 'Entretien préventif',
  controle: 'Contrôle',
  mise_en_service: 'Mise en service',
  installation: 'Montage / installation',
};

export const STATUTS_BON = ['brouillon', 'signe'] as const;
export type StatutBon = typeof STATUTS_BON[number];
export const LIBELLES_STATUT_BON: Record<StatutBon, string> = { brouillon: 'En cours (non signé)', signe: 'Signé' };

// ─── GTB / immotique / domotique ───
export const PROTOCOLES = ['KNX', 'KNX IP', 'Modbus RTU', 'Modbus TCP', 'BACnet IP', 'BACnet MS/TP', 'M-Bus', 'DALI', 'LON', 'IP', 'Autre'] as const;
export const ACCES_POINT = ['lecture', 'ecriture', 'lecture_ecriture'] as const;
export const LIBELLES_ACCES_POINT: Record<typeof ACCES_POINT[number], string> = { lecture: 'Lecture', ecriture: 'Écriture', lecture_ecriture: 'Lecture / écriture' };
export const RESULTATS_TEST = ['ok', 'ko', 'non_teste'] as const;
export type ResultatTest = typeof RESULTATS_TEST[number];
export const LIBELLES_RESULTAT: Record<ResultatTest, string> = { ok: 'OK', ko: 'KO', non_teste: 'Non testé' };

/** Documents du dossier des ouvrages exécutés proposés à cocher (la liste se complète librement). */
export const DOE_PROPOSES = [
  'Synoptique / architecture du réseau',
  'Liste des points (table d’échange)',
  'Plan d’adressage (IP, KNX, Modbus, BACnet)',
  'Projet de programmation (export ETS, fichier de configuration)',
  'Notices et fiches techniques des matériels',
  'Procès-verbal d’essais',
  'Identifiants d’accès remis au client (hors bon : coffre du site)',
] as const;

// ─── CVC / fluides frigorigènes (rubriques du Cerfa 15497) ───
/** Version du formulaire relevée le 31/10/2026 (service-public.fr, arrêté du 29/02/2016 art. 11). Le ministère annonce des évolutions : à revoir. */
export const CERFA_FICHE_INTERVENTION = 'Cerfa n° 15497*04';
export const NATURES_INTERVENTION_FLUIDE = ['assemblage', 'mise_en_service', 'modification', 'maintenance', 'etancheite_periodique',
  'etancheite_non_periodique', 'demantelement', 'autre'] as const;
export type NatureInterventionFluide = typeof NATURES_INTERVENTION_FLUIDE[number];
export const LIBELLES_NATURE_FLUIDE: Record<NatureInterventionFluide, string> = {
  assemblage: 'Assemblage de l’équipement',
  mise_en_service: 'Mise en service de l’équipement',
  modification: 'Modification de l’équipement',
  maintenance: 'Maintenance de l’équipement',
  etancheite_periodique: 'Contrôle d’étanchéité périodique',
  etancheite_non_periodique: 'Contrôle d’étanchéité non périodique',
  demantelement: 'Démantèlement',
  autre: 'Autre',
};
/** Famille réglementaire du fluide : elle fixe l'unité du seuil (t éq. CO2 ou kg). */
export const FAMILLES_FLUIDE = ['hfc_pfc', 'hfo', 'hcfc', 'autre'] as const;
export type FamilleFluide = typeof FAMILLES_FLUIDE[number];
export const LIBELLES_FAMILLE_FLUIDE: Record<FamilleFluide, string> = {
  hfc_pfc: 'HFC / PFC (y compris mélange HFC + HFO)', hfo: 'HFO (annexe II, section 1 du règlement (UE) 2024/573)', hcfc: 'HCFC', autre: 'Autre / à vérifier',
};

export interface Periodicite { mois: number | null; classe: string; base: string; aVerifier: boolean }

/**
 * Fréquence minimale du contrôle d'étanchéité, d'après le règlement (UE) 2024/573 art. 5 § 6 (HFC/PFC en t éq. CO2, HFO en kg)
 * et l'arrêté du 29/02/2016 art. 4 (HCFC, en kg). Indicative : l'opérateur la confirme (exemptions des équipements hermétiquement
 * scellés et des bâtiments résidentiels non traitées ici). Rien n'est rendu quand une donnée manque.
 */
export function periodiciteEtancheite(o: { famille: FamilleFluide | null; chargeKg: number | null; teqCo2: number | null; detectionPermanente: boolean | null }): Periodicite {
  const det = o.detectionPermanente === true;
  const r = (mois: number | null, classe: string, base: string, aVerifier = false): Periodicite => ({ mois, classe, base, aVerifier });
  if (o.famille === 'hfc_pfc') {
    if (o.teqCo2 === null) return r(null, 'Tonnage équivalent CO2 inconnu (PRG du fluide à renseigner)', '', true);
    const base = 'Règlement (UE) 2024/573, art. 5 § 6';
    if (o.teqCo2 < 5) return r(null, 'Moins de 5 t éq. CO2 : pas de contrôle périodique imposé par ce seuil', base, true);
    if (o.teqCo2 < 50) return r(det ? 24 : 12, '5 à moins de 50 t éq. CO2', base);
    if (o.teqCo2 < 500) return r(det ? 12 : 6, '50 à moins de 500 t éq. CO2', base);
    return r(det ? 6 : 3, '500 t éq. CO2 ou plus (détection des fuites obligatoire)', base);
  }
  if (o.famille === 'hfo') {
    if (o.chargeKg === null) return r(null, 'Charge inconnue', '', true);
    const base = 'Règlement (UE) 2024/573, art. 5 § 6 (gaz de l’annexe II, section 1)';
    if (o.chargeKg < 1) return r(null, 'Moins de 1 kg : pas de contrôle périodique imposé par ce seuil', base, true);
    if (o.chargeKg < 10) return r(det ? 24 : 12, '1 à moins de 10 kg', base);
    if (o.chargeKg < 100) return r(det ? 12 : 6, '10 à moins de 100 kg', base);
    return r(det ? 6 : 3, '100 kg ou plus (détection des fuites obligatoire)', base);
  }
  if (o.famille === 'hcfc') {
    if (o.chargeKg === null) return r(null, 'Charge inconnue', '', true);
    const base = 'Arrêté du 29 février 2016, art. 4';
    if (o.chargeKg < 2) return r(null, 'Moins de 2 kg : pas de contrôle périodique imposé par ce seuil', base, true);
    if (o.chargeKg < 30) return r(12, '2 à moins de 30 kg', base);
    if (o.chargeKg < 300) return r(6, '30 à moins de 300 kg', base);
    return r(3, '300 kg ou plus', base);
  }
  return r(null, 'Famille du fluide à préciser', '', true);
}

/** Tonnage équivalent CO2 = charge (kg) × PRG / 1000 (règlement (UE) 2024/573, art. 3 point 3), arrondi à 0,01 t. */
export function tonnesEqCo2(chargeKg: number | null, prg: number | null): number | null {
  if (chargeKg === null || prg === null || !Number.isFinite(chargeKg) || !Number.isFinite(prg)) return null;
  return Math.round(chargeKg * prg / 10) / 100;
}

// ─── Forme des sections (stockées en JSON, contrôlées par le serveur) ───
export interface PieceBon { designation: string; reference: string | null; numero_serie: string | null; quantite: string | null; mouvement: 'posee' | 'remplacee' | 'deposee' }
export interface PasserelleBon {
  equipement: string; marque: string | null; modele: string | null; protocoles: string[]; adresse: string | null;
  firmware_avant: string | null; firmware_apres: string | null; numero_serie: string | null; remarque: string | null;
}
export interface PointBon { repere: string; libelle: string | null; acces: typeof ACCES_POINT[number] | null; valeur: string | null; resultat: ResultatTest }
export interface EssaiBon { essai: string; attendu: string | null; obtenu: string | null; resultat: ResultatTest }
export interface DoeBon { document: string; remis: boolean }
export interface FluidesBon {
  operateur: { nom: string | null; adresse: string | null; siret: string | null; attestation_capacite: string | null };
  detenteur: { nom: string | null; adresse: string | null; siret: string | null };
  equipement: { identification: string | null; fluide_id: string | null; fluide: string | null; famille: FamilleFluide | null; prg: number | null; prg_source: string | null; charge_totale_kg: number | null };
  natures: NatureInterventionFluide[]; nature_autre: string | null;
  detecteur: { identification: string | null; date_controle: string | null };
  detection_permanente: boolean | null;
  fuites: { constatees: boolean | null; lignes: { localisation: string; reparation: 'realisee' | 'a_faire' }[] };
  charge: { vierge_kg: number | null; recycle_kg: number | null; regenere_kg: number | null };
  recupere: { traitement_kg: number | null; reutilisation_kg: number | null; contenant: string | null; bsff: string | null };
  destination: string | null;
  observations: string | null;
  /** Intervenant fluides (31/10/2026, migration 050) : choisi à l'écran, complété et recopié PAR LE SERVEUR, figé à la signature. */
  intervenant?: IntervenantFluides | null;
}

// ─── Intervenant fluides (décision du dirigeant du 31/10/2026) ───
/** Attestation (aptitude d'un technicien, capacité d'une entreprise) telle que recopiée dans le bon. */
export interface AttestationFluides { numero: string | null; categorie?: string | null; organisme: string | null; fin_validite: string | null }
/**
 * « interne » : technicien de la société, NOMMÉ (compte de la société), son attestation d'aptitude et l'attestation de capacité de la
 * société ; « sous_traitant » : entreprise sous-traitante enregistrée, qui intervient au nom de la société et porte la fiche et la
 * déclaration, avec son attestation de capacité, le nom de SON technicien et le numéro de son attestation d'aptitude.
 */
export type IntervenantFluides =
  | { type: 'interne'; user_id: string | null; nom?: string | null; aptitude?: AttestationFluides | null; capacite?: (AttestationFluides & { titulaire: string | null }) | null }
  | { type: 'sous_traitant'; sous_traitant_id: string | null; raison_sociale?: string | null; siret?: string | null; adresse?: string | null;
      capacite?: AttestationFluides | null; technicien_nom: string | null; technicien_attestation: string | null };
export const LIBELLES_INTERVENANT = { interne: 'Technicien de la société', sous_traitant: 'Sous-traitant' } as const;
export const PHRASE_SOUS_TRAITANT = 'Le sous-traitant intervient au nom de la société. La déclaration annuelle des fluides de cette intervention est à voir avec le sous-traitant.';

export interface SectionsBon {
  pieces?: PieceBon[];
  passerelles?: PasserelleBon[];
  points?: PointBon[];
  essais?: EssaiBon[];
  reserves?: string | null;
  doe?: DoeBon[];
  fluides?: FluidesBon | null;
  /** Ajouts / déductions par rapport au devis lié (051) : seulement si un devis accepté est lié ; figés à la signature. */
  ajustements_devis?: AjustementDevis[];
}

// ─── Lien avec le devis accepté et ajouts / déductions (décision du dirigeant du 31/10/2026, migration 051) ───
// Le bon ne porte AUCUN prix du devis : seulement sa référence, son intitulé et sa date d'acceptation. Les seuls montants d'un bon sont
// les ajouts et déductions constatés sur place (raison + montant HT), saisis sur CE bon ; le bon ne calcule pas le total du devis.
export const SENS_AJUSTEMENT = ['ajout', 'deduction'] as const;
export type SensAjustement = typeof SENS_AJUSTEMENT[number];
export const LIBELLES_SENS_AJUSTEMENT: Record<SensAjustement, string> = { ajout: 'Ajout', deduction: 'Déduction' };
/** Montant HT : chaîne à 2 décimales exactement (« 85.00 »), strictement positif ; le sens donne le signe. */
export interface AjustementDevis { sens: SensAjustement; raison: string; montant_ht: string }
export const RAISON_AJUSTEMENT_MAX = 500;
export const MONTANT_AJUSTEMENT_MAX_CENTIMES = 999_999_999;   // 9 999 999,99 € HT
/** Ce que le bon recopie du devis accepté (contenu signé, PDF) : jamais un montant. */
export interface DevisDuBon { numero: string; revision: number; variante: string; intitule: string; accepte_le: string }
export const libelleDevis = (d: DevisDuBon) => `${d.numero} — révision ${d.revision}${d.variante && d.variante !== 'Base' ? ` (${d.variante})` : ''}`;

/**
 * Montant HT saisi → centimes (entier), ou null s'il est invalide : nombre ou texte, virgule ou point, espaces ignorés, 2 décimales
 * au plus, strictement positif, 9 999 999,99 au plus. Aucun arrondi : « 12,345 » est refusé plutôt qu'arrondi.
 */
export function centimesMontant(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const s = (typeof v === 'number' ? (Number.isFinite(v) ? String(v) : '') : String(v)).replace(/[\s  ]/g, '').replace(',', '.');
  const m = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const c = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  return c > 0 && c <= MONTANT_AJUSTEMENT_MAX_CENTIMES ? c : null;
}
export const montantDepuisCentimes = (c: number) => `${Math.trunc(c / 100)}.${String(Math.abs(c) % 100).padStart(2, '0')}`;
/** Centimes signés d'une ligne (ajout +, déduction −). */
export const centimesSignes = (l: { sens: SensAjustement; montant_ht: string | number }) => (centimesMontant(l.montant_ht) ?? 0) * (l.sens === 'deduction' ? -1 : 1);
/** Total net des lignes, en centimes signés (calcul entier : aucun écart d'arrondi). */
export const totalNetCentimes = (l: { sens: SensAjustement; montant_ht: string | number }[]) => l.reduce((t, x) => t + centimesSignes(x), 0);
/** « +85,00 € HT », « −40,00 € HT » (signe toujours écrit, espace insécable avant €). */
export function formatMontantSigne(c: number) {
  const a = Math.abs(c);
  const ent = String(Math.trunc(a / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${c < 0 ? '−' : c > 0 ? '+' : ''}${ent},${String(a % 100).padStart(2, '0')} € HT`;
}

export const MOTIFS_ABSENCE = ['absent', 'refus'] as const;
export type MotifAbsence = typeof MOTIFS_ABSENCE[number];
/** Tracé de signature : traits de points normalisés (0..1 en largeur, 0..ratio en hauteur), relevés au doigt ou à la souris. */
export interface TraceSignature { largeur: number; hauteur: number; traits: [number, number][][] }

/** Clé de comparaison d'un n° de série (lien avec « Ma gamme ») : espaces de bord retirés, casse ignorée. Même règle que le serveur. */
export const cleSerie = (n: unknown) => String(n ?? '').trim().toLowerCase();
